
import React, { useEffect, useState } from 'react';
import { getPublicStudios, getAnnouncements, getServices } from '../services/dataService';
import { Announcement } from '../types';
import { Megaphone, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const AnnouncementBar: React.FC = () => {
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [targetUrl, setTargetUrl] = useState<string>('/services');
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Find the Platform Owner (to show their announcements globally)
                const studios = await getPublicStudios();
                const owner = studios.find(u => u.role === 'OWNER');
                
                if (owner) {
                    // 2. Fetch Announcements
                    const all = await getAnnouncements(owner.uid);
                    
                    // 3. Filter for Active
                    const active = all.filter(a => a.active);
                    
                    if (active.length > 0) {
                        const ann = active[0];
                        setAnnouncement(ann);

                        // 4. Resolve correct Link URL based on Service Type
                        if (ann.linkedServiceId) {
                            const services = await getServices();
                            const service = services.find(s => s.id === ann.linkedServiceId);
                            
                            if (service) {
                                if (service.quoteFormType && service.quoteFormType !== 'NONE') {
                                    setTargetUrl(`/quote/${service.id}`);
                                } else if (service.isBookable) {
                                    setTargetUrl(`/book/${service.id}`);
                                } else {
                                    setTargetUrl(`/order/${service.id}`);
                                }
                            } else {
                                setTargetUrl('/services'); // Fallback if service deleted
                            }
                        } else {
                            setTargetUrl('/services');
                        }
                    }
                }
            } catch (e) {
                console.error("Failed to load announcements", e);
            }
        };
        fetchData();
    }, []);

    if (!announcement || !isVisible) return null;

    return (
        <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 text-white px-4 py-2 relative z-[60] border-b border-white/10 shadow-lg animate-in slide-in-from-top duration-500">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 overflow-hidden">
                    <div className="bg-white/10 p-1.5 rounded-full shrink-0 animate-pulse">
                        <Megaphone size={14} className="text-white" />
                    </div>
                    <p className="text-xs md:text-sm font-medium tracking-wide truncate">
                        {announcement.content}
                    </p>
                    {announcement.linkedServiceId && (
                        <Link 
                            to={targetUrl} 
                            className="hidden md:flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-white/10 hover:bg-white/20 px-2 py-1 rounded transition-colors whitespace-nowrap"
                        >
                            {announcement.linkedServiceName ? `View ${announcement.linkedServiceName}` : 'View Offer'} <ArrowRight size={10} />
                        </Link>
                    )}
                </div>
                <button 
                    onClick={() => setIsVisible(false)} 
                    className="text-white/50 hover:text-white transition-colors p-1"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
};
